// MP411 — the public hosts, resolved once for the customer web app.
//
// Reads `VITE_PUBLIC_APP_URL`, `VITE_PUBLIC_API_URL`,
// `VITE_PUBLIC_MARKETING_URL`, `VITE_PUBLIC_GATEWAY_URL`,
// `VITE_PUBLIC_RESILIENCE_HOST`, `VITE_BRAND_DOMAIN` (the api's names
// with the Vite prefix — web.yml passes them from the environment's
// `vars`). The variables this app read before MP411 keep working with
// lower precedence: `VITE_API_URL` (api) and `VITE_AI_GATEWAY_URL`
// (gateway console). Unset → the defaults in
// packages/shared-types/src/hosts.ts, i.e. what dev serves today.
//
// Every page that needs an origin or a brand mailbox imports from here
// (legal pages, status, pricing, footer, tenantHost, apps.ts). No
// literal domain may appear elsewhere in apps/web/src —
// scripts/check-public-hosts.mjs enforces it in CI.

import {
  brandMailbox,
  brandMailto,
  buildPublicLink,
  resolvePublicHosts,
  viteEnvToPublicHostEnv,
  type PublicHosts,
} from '@cyber-aware/shared-types';

const resolved = resolvePublicHosts(
  // `?? {}`: outside Vite (a tsx unit test importing lib/apps) there is no
  // import.meta.env; fall back to the shared defaults instead of throwing.
  viteEnvToPublicHostEnv((import.meta.env ?? {}) as Record<string, string | undefined>, {
    apiUrl: 'VITE_API_URL',
    gatewayUrl: 'VITE_AI_GATEWAY_URL',
  }),
);

if (resolved.issues.length && typeof console !== 'undefined') {
  for (const issue of resolved.issues) {
    console.warn(`[public-hosts] VITE_${issue.key}="${issue.value}" ignored (${issue.reason}); using the default.`);
  }
}

export const PUBLIC_HOSTS: Readonly<PublicHosts> = resolved.hosts;

/** Absolute link on the customer app (or another surface). */
export function publicLink(path?: string, surface?: Parameters<typeof buildPublicLink>[2]): string {
  return buildPublicLink(PUBLIC_HOSTS, path, surface);
}

/** `local@<BRAND_DOMAIN>` — legal@, privacy@, billing@, status@, hello@. */
export function brandEmail(localPart: string): string {
  return brandMailbox(PUBLIC_HOSTS, localPart);
}

/** `mailto:` href for a brand mailbox. */
export function brandEmailHref(localPart: string, subject?: string | null): string {
  return brandMailto(PUBLIC_HOSTS, localPart, subject);
}
